/**
 * Projects — read model for the `/projects` surface.
 *
 * Defines the `ProjectListState` shape (filters + sort + paging + search +
 * view tab + group-by + visible properties), its `defaultState`, and the
 * round-trippable URL-state codec used by `useProjectsListVM`. URL state is
 * the single source of truth — refreshing or pasting the URL restores the
 * exact view.
 *
 * Mirrors `models/documents.ts` so `/projects` shares the Documents toolbar
 * grammar: Search · + Filter · active chips · spacer · Group by · Properties.
 * The Projects list is small (dozens of rows per org), so unlike Documents —
 * which pushes search / filter / sort / paging to a `list_documents` RPC — the
 * VM filters, sorts, groups and paginates the already-loaded rows client-side.
 * Sort uses the Work Items encoding (`?sort=-created_at`) so column-header
 * click-to-sort can swap directions without round-tripping through a dropdown.
 */
import type { SortDescriptor } from '../../components/Table';

/** Page size for the client-side Projects pager. */
export const PAGE_SIZE = 24;

/** View tabs on `/projects`. */
export type ProjectsTab = 'gallery' | 'table';

/** Group-by axes shared by the Gallery and Table views. */
export type ProjectsGroupBy = 'none' | 'status' | 'lead';

/** Stable column key → sortable on the Table view (client-side). */
export type ProjectSortKey =
  | 'name'
  | 'status'
  | 'progress'
  | 'created_at'
  | 'target_date';

/**
 * Filter facet keys used by the + Filter popover and the chip × buttons.
 * Both are multi-value: `status` over the project status string, `lead` over
 * the lead user id.
 */
export type FilterFacetKey = 'status' | 'lead';

export interface ProjectListState {
  /** `projects.status` values. */
  status: ReadonlyArray<string>;
  /** `projects.lead_id` values (lead facet). */
  lead: ReadonlyArray<string>;
  /** Substring search over name + description (client-side). */
  q: string;
  /** Column + direction. Encoded as `?sort=[-]<column>` (Work Items style). */
  sort: SortDescriptor;
  /** 1-based page index. */
  page: number;
  /** Active view. */
  tab: ProjectsTab;
  /** Group-by axis (Table dividers, Gallery section headers). */
  groupBy: ProjectsGroupBy;
  /**
   * Visible properties — table column keys when `tab === 'table'`, card chip
   * keys when `tab === 'gallery'`. Same `?cols=` URL param round-trips both.
   * An empty array means "use the per-tab defaults".
   */
  cols: ReadonlyArray<string>;
}

export const DEFAULT_SORT: SortDescriptor = {
  columnKey: 'created_at',
  direction: 'desc'
};

export const defaultState: ProjectListState = Object.freeze({
  status: [],
  lead: [],
  q: '',
  sort: DEFAULT_SORT,
  page: 1,
  tab: 'gallery' as ProjectsTab,
  groupBy: 'none' as ProjectsGroupBy,
  cols: []
});

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  const fromComma = raw.split(',').map((s) => s.trim()).filter(Boolean);
  if (fromComma.length > 1) return fromComma;
  const repeated = params.getAll(key).flatMap((v) =>
    v.split(',').map((s) => s.trim()).filter(Boolean)
  );
  return repeated.length > 0 ? repeated : fromComma;
}

function readPage(params: URLSearchParams): number {
  const raw = params.get('page');
  if (!raw) return 1;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function readSort(params: URLSearchParams): SortDescriptor {
  const raw = params.get('sort');
  if (!raw) return DEFAULT_SORT;
  const desc = raw.startsWith('-');
  const key = desc ? raw.slice(1) : raw;
  if (!key) return DEFAULT_SORT;
  return { columnKey: key, direction: desc ? 'desc' : 'asc' };
}

function readTab(params: URLSearchParams): ProjectsTab {
  const raw = params.get('tab');
  return raw === 'table' ? 'table' : 'gallery';
}

function readGroupBy(params: URLSearchParams): ProjectsGroupBy {
  const raw = params.get('groupBy');
  switch (raw) {
    case 'status':
    case 'lead':
      return raw;
    default:
      return 'none';
  }
}

/**
 * Decode a `URLSearchParams` snapshot to a full state. Unknown / invalid
 * values fall back to the corresponding default — the URL is permissive so
 * bad share-links never crash the page.
 */
export function parseSearchParams(params: URLSearchParams): ProjectListState {
  return {
    status: readList(params, 'status'),
    lead: readList(params, 'lead'),
    q: params.get('q')?.trim() ?? '',
    sort: readSort(params),
    page: readPage(params),
    tab: readTab(params),
    groupBy: readGroupBy(params),
    cols: readList(params, 'cols')
  };
}

function encodeSort(sort: SortDescriptor): string {
  return sort.direction === 'desc' ? `-${sort.columnKey}` : sort.columnKey;
}

function sortIsDefault(sort: SortDescriptor): boolean {
  return (
    sort.columnKey === DEFAULT_SORT.columnKey &&
    sort.direction === DEFAULT_SORT.direction
  );
}

/**
 * Encode a state to a `URLSearchParams`. Default-equal values are omitted so
 * the canonical URL for the empty state is `/projects` (no `?`).
 */
export function stateToSearchParams(state: ProjectListState): URLSearchParams {
  const p = new URLSearchParams();
  if (state.status.length > 0) p.set('status', state.status.join(','));
  if (state.lead.length > 0) p.set('lead', state.lead.join(','));
  if (state.q) p.set('q', state.q);
  if (!sortIsDefault(state.sort)) p.set('sort', encodeSort(state.sort));
  if (state.page > 1) p.set('page', String(state.page));
  if (state.tab !== 'gallery') p.set('tab', state.tab);
  if (state.groupBy !== 'none') p.set('groupBy', state.groupBy);
  if (state.cols.length > 0) p.set('cols', state.cols.join(','));
  return p;
}

/**
 * True when `state` has any active filter or search — drives the
 * "no filters set" empty-state branch.
 */
export function hasActiveFilters(state: ProjectListState): boolean {
  return (
    state.status.length > 0 ||
    state.lead.length > 0 ||
    state.q.length > 0
  );
}
